import moment from 'moment'
import { createSelector } from 'reselect'
import { filterPromotionsByTravelPeriod } from 'lib/hotels/bedbankUtils'
import { MONTH_NAMES } from 'constants/dates'
import { addDays, dateIsBefore, diffInDays, pad } from 'lib/datetime/dateUtils'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { min, max, fillArray } from 'lib/array/arrayUtils'
import { getFlightCalendar } from 'selectors/flightsSelectors'
import { getBedbankCalendarKey } from 'actions/BedbankOfferActions'
import config from 'constants/config'
import { BEDBANK_CALENDAR_FUTURE_DAYS } from 'lib/datetime/calendarUtils'
import { SALES_ENVIRONMENT_ENUM } from 'constants/bedbank'
import { Bedbank } from '@luxuryescapes/contract-svc-bedbank'
import { ISO_DATE_FORMAT, ISO_DATE_SHORT_FORMAT } from 'constants/dateFormats'

const FLIGHT_MAX_DAYS_IN_FUTURE = 310
const maxFlightDate = moment().add(FLIGHT_MAX_DAYS_IN_FUTURE, 'days').toDate()
const LOS_BASED_CACHE_BUFFER = 16

export function getPromotionMinLos(offer?: App.BedbankOffer) {
  if (offer) {
    return min(offer.promotions.map(x => x.los)) ?? 0
  }

  return 0
}

export const getActivePromotions = createSelector(
  (state: OfferPageState) => state.bedbankOffer,
  (state: OfferPageState) => state.duration,
  (offer, duration): Array<App.BedbankPromotion> => {
    if (!offer) return []
    return offer.promotions.filter(promo => duration && promo.los <= duration)
  },
)

interface BaseCalendarDay {
  day: number;
  month: string;
  year: string;
  monthKey: string;
  checkIn: string;
  checkOut: string;
  hasPromo: boolean;
}

interface BaseCalendarMonth {
  key: string;
  month: string;
  year: number;
  days: Array<BaseCalendarDay>;
  hasPromo: boolean;
  cheapestDay: BaseCalendarDay | null;
}

interface BaseCalendar {
  [monthKey: string]: BaseCalendarMonth;
}

export const getBaseCalendar = createSelector(
  getActivePromotions,
  (state: OfferPageState) => state.duration,
  (promotions, duration) => {
    const output: BaseCalendar = {}

    for (let day = 0; day <= BEDBANK_CALENDAR_FUTURE_DAYS; day++) {
      const currentDay = addDays(new Date(), day)
      // use local date format to avoid timezone jump issues
      const month = currentDay.getMonth()
      const year = currentDay.getFullYear()
      const date = currentDay.getDate()
      const monthText = MONTH_NAMES[month]
      const monthKey = `${year}-${pad(month + 1)}`
      const checkIn = moment(currentDay).format(ISO_DATE_FORMAT)
      const checkOut = moment(addDays(currentDay, duration || 1)).format(ISO_DATE_FORMAT)
      const hasPromo = promotions.some(promo => filterPromotionsByTravelPeriod(promo, checkIn, checkOut))

      // create month key object if it doesn't exist
      if (!output[monthKey]) {
        output[monthKey] = {
          key: monthKey,
          month: monthText,
          year,
          days: [] as Array<BaseCalendarDay>,
          hasPromo,
          cheapestDay: null,
        }
      }

      // update with current day
      output[monthKey].days.push({
        day: date,
        month: monthText,
        year: year.toString(),
        checkIn,
        checkOut,
        hasPromo,
      } as BaseCalendarDay)
      output[monthKey].hasPromo = output[monthKey].hasPromo || hasPromo
    }

    return output
  },
)

export const getBedbankCalendar = createSelector(
  (state: OfferPageState) => getPromotionMinLos(state.bedbankOffer),
  (state: OfferPageState) => state.duration,
  (state: OfferPageState) => state.bedbankOffer,
  (_state: OfferPageState, appState: App.State) => appState.offer.bedbankOfferCalendar,
  (_state: OfferPageState, appState: App.State) => appState.geo.currentRegionCode,
  (state: OfferPageState) => state.selectedBedbankRoomId,
  (promotionMinLos, duration, offer, bedbankOfferCalendar, region, selectedBedbankRoomId): App.CalendarResponse | undefined => {
    return bedbankOfferCalendar[offer?.id ?? '']?.[getBedbankCalendarKey(region, duration || promotionMinLos, selectedBedbankRoomId)]
  },
)

export const isAnyCalendarExist = createSelector(
  (state: OfferPageState) => state.bedbankOffer,
  (_state: OfferPageState, appState: App.State) => appState.offer.bedbankOfferCalendar,
  (_state: OfferPageState, appState: App.State) => appState.geo.currentRegionCode,
  (offer, bedbankOfferCalendar, region) => {
    if (config.BEDBANK_NEW_CALENDAR && offer && offer.promotions.length > 0) {
      const calendarsForOffer = bedbankOfferCalendar[offer?.id ?? ''] ?? {}
      // find any non empty calendar in this region for this offer
      return !!Object.entries(calendarsForOffer).find(([key, calendar]) => {
        return key.includes(region) && calendar?.[SALES_ENVIRONMENT_ENUM.hotelOnly]?.rooms?.length
      })
    }

    return false
  },
)

export const isBedbankCalendarFetching = createSelector(
  (state: OfferPageState, appState: App.State) => appState.offer.bedbankOfferCalendarLoading[state.bedbankOffer!.id],
  (fetching) => !!fetching,
)

export const getCalendarDates = createSelector(
  getBaseCalendar,
  getActivePromotions,
  (state: OfferPageState, appState: App.State) => getBedbankCalendar(state, appState),
  (state: OfferPageState, appState: App.State) => getFlightCalendar(state, appState),
  (state: OfferPageState) => state.flights,
  (state: OfferPageState) => state.rooms,
  (baseCalendarDates, promotions, calendar, flightsCalendar, showPackagePrices, rooms) => {
    const copyCalendar: { [month: string]: App.BedbankCalendarMonth } = JSON.parse(JSON.stringify(baseCalendarDates))

    if (calendar && baseCalendarDates && promotions.length > 0) {
      if (flightsCalendar) {
        for (const checkIn of Object.keys(flightsCalendar)) {
          const monthKey = moment(checkIn).format(ISO_DATE_SHORT_FORMAT)
          if (copyCalendar[monthKey]) {
            const dayData = copyCalendar[monthKey].days.find(x => x.checkIn === checkIn)
            if (dayData?.hasPromo && flightsCalendar[checkIn].cost && dateIsBefore(new Date(dayData.checkOut), maxFlightDate)) {
              dayData.flightPrice = flightsCalendar[checkIn].cost
            }
          }
        }
      }

      const salesEnv = showPackagePrices ? SALES_ENVIRONMENT_ENUM.hotelPackage : SALES_ENVIRONMENT_ENUM.hotelOnly
      for (const day of calendar[salesEnv].dates) {
        const monthKey = moment(day.checkIn).format(ISO_DATE_SHORT_FORMAT)
        if (copyCalendar[monthKey]) {
          const dayData = copyCalendar[monthKey].days.find(x => x.checkIn === day.checkIn)
          if (dayData) {
            dayData.isCorporate = day.isCorporate
            if (dayData?.hasPromo) {
              dayData.price = day.inclusive * (rooms?.length ?? 1)
              dayData.taxesAndFees = day.taxesAndFees * (rooms?.length ?? 1)
              dayData.propertyFees = day.propertyFees * (rooms?.length ?? 1)
              copyCalendar[monthKey].cheapestDay = min(copyCalendar[monthKey].days.filter(d => d.price), d => d.price ?? 0)
              copyCalendar[monthKey].cheapestDayWithFlights = min(copyCalendar[monthKey].days.filter(d => d.price), d => (d.price ?? 0) + (d.flightPrice ?? 0))
            }
          }
        }
      }
    }

    return copyCalendar
  },
)

export const getSelectedDay = createSelector(
  getCalendarDates,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.checkOutDate,
  (calendarDates, checkIn, checkOut) => {
    if (checkIn && checkOut && calendarDates) {
      const key = moment(checkIn).format(ISO_DATE_SHORT_FORMAT)
      if (calendarDates[key]) {
        return calendarDates[key].days.find((day) => day.checkIn === checkIn)
      }
    }
    return undefined
  },
)

type BedbankRoomLos = Bedbank.CalendarResponseRoom['los'][0]

export const generateDurations = createSelector(
  (state: OfferPageState) => getPromotionMinLos(state.bedbankOffer),
  getActivePromotions,
  getSelectedDay,
  getBedbankCalendar,
  (minLOS, promotions, selectedDay, calendarData) => {
    const defaultDayRestriction = minLOS + LOS_BASED_CACHE_BUFFER
    let dayRestriction = defaultDayRestriction
    let lowestRoomDurations: Record<number, BedbankRoomLos | undefined> = {}
    if (promotions) {
      const lastDate = max(promotions.map(x => x.travelPeriod.to), (d) => new Date(d).getDate())
      if (lastDate) {
        const allDays = diffInDays(new Date(), new Date(lastDate))
        if (!selectedDay && allDays <= (dayRestriction * 2)) {
          if (allDays > minLOS) {
            dayRestriction = allDays
          } else {
            dayRestriction = 0
          }
        } else if (selectedDay) {
          const daysCheckout = diffInDays(new Date(selectedDay.checkIn), new Date(lastDate))
          if (daysCheckout > 0) {
            dayRestriction = daysCheckout
          } else {
            dayRestriction = 0
          }
        }
      }
    }

    if (calendarData?.[SALES_ENVIRONMENT_ENUM.hotelOnly]?.rooms.length) {
      lowestRoomDurations = fillArray(dayRestriction).reduce<Record<number, BedbankRoomLos | undefined>>((acc, _, index) => {
        acc[index + 1] = min(calendarData[SALES_ENVIRONMENT_ENUM.hotelOnly].rooms.flatMap(r => r.los[index + 1]), (r) => r?.inclusive)
        return acc
      }, {})
    }

    return Array.from({ length: defaultDayRestriction }).map((_, index) => ({
      duration: index + 1,
      hasPromo: minLOS <= index + 1 && dayRestriction >= index + 1,
      price: (minLOS <= index + 1 && lowestRoomDurations?.[index + 1]?.inclusive),
      propertyFees: (minLOS <= index + 1 && lowestRoomDurations?.[index + 1]?.propertyFees),
      taxesAndFees: (minLOS <= index + 1 && lowestRoomDurations?.[index + 1]?.taxesAndFees),
      flightPrice: 0,
    }))
  },
)

export const isFlashCalendarEnabled = createSelector(
  (state: OfferPageState) => state.bedbankOffer,
  (state: OfferPageState, appState: App.State) => getBedbankCalendar(state, appState),
  (offer, calendar) => {
    if (config.BEDBANK_NEW_CALENDAR && offer && calendar) {
      return calendar?.[SALES_ENVIRONMENT_ENUM.hotelOnly]?.rooms?.length > 0 && offer.promotions.length > 0
    }
    return false
  },
)
